import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip } from "primereact/tooltip";
import { useConfigure } from "../../../Hooks/Features/Task/useConfigure";
import { SkeletonContainer, SkeletonLine } from "../../../components/Loader/Loader";
import { Dialog } from "primereact/dialog";
import { MultiSelect } from "primereact/multiselect";
import PanelHeaderTemplate from "../../../components/CommonCompo/PanelHeadeTemplate/PanelHeaderTemplate";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, useDroppable, DragOverlay } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";

import { CSS } from "@dnd-kit/utilities";

const SortableField = ({ field, area, removeFromSection, collapsedId }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: field?.custom_field_id, data: { area, field } });

    return (
        <div ref={setNodeRef} style={{ transform: CSS.Transform?.toString(transform), transition, opacity: isDragging ? 0.4 : 1, cursor: "grab" }} {...attributes} {...listeners} className={`rounded field-box d-flex justify-content-between align-items-center bg-white border ${collapsedId === field?.custom_field_id ? "drag-source-hidden" : "p-2 py-1 my-2"}`} >
            <span className="fw-semibold"><FontAwesomeIcon icon="fa-solid fa-grip-vertical" className="text-muted me-3" />{field?.custom_field_name}</span>

            <div className="d-flex align-items-center gap-4">
                <button type="button" className="sk-btn sk-btn-primary btn-sm ms-2 px-2" onClick={() => removeFromSection(field?.custom_field_id, area)}> Remove</button>
            </div>
        </div>
    );
};

const DroppableSection = ({ area, children, onDrop, onDragOver, setHoverArea, hoverArea }) => {
    const { setNodeRef } = useDroppable({ id: area });
    return (
        <div ref={setNodeRef} className={"border p-2 rounded mb-3" + (hoverArea === area ? " section-hover" : "")} onDragOver={(e) => { e.preventDefault(); onDragOver(e, area); }} onDragLeave={() => setHoverArea(null)} onDrop={(e) => { e.preventDefault(); onDrop(e, area); }} style={{ minHeight: "100px", transition: "all 0.2s" }} >
            {children}
        </div>
    );
};

const Configure = ({ filterValues, setFilterValues }) => {
    const { onDragStart, onDragOver, onDrop, usedIds, hoverArea, setHoverArea, displayJson, setDisplayJson, fields, setFields, removeFromSection, onDrag, dragPreview, loader, onDragEnd, saveLayout, copy, setCopy, copyLayout, collapsedId, activeField, setActiveField, handleDndDragEnd, handleDndDragOver } = useConfigure({ filterValues, setFilterValues });

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5
            }
        })
    );

    return (
        <div className="configure">
            <div className='d-flex justify-content-between gap-1 mb-2 border-bottom ms-2'>
                <div className='d-flex gap-2 align-items-center'>
                    <button className='sk-btn sk-btn-primary' onClick={() => setFilterValues((prev) => ({ ...prev, layout: {}, apiCall: true }))}><FontAwesomeIcon icon="fa-solid fa-angle-left" /></button>
                    <div className='fw-bold'>Work item layout for "{filterValues?.layout?.screen_scheme_name}"</div>
                </div>
            </div>
            <div className="d-flex">
                {/* Left: Configuration Areas */}
                <div className="w70 m-2 d-flex flex-column" style={{ height: "calc(100vh - 165px)" }}>
                    {loader?.area ?
                        <div style={{ height: "100%", overflowY: "auto" }}>
                            {Array?.from({ length: 3 })?.map((item, idx) =>
                                <div key={idx} className="mb-3" style={{ height: "100px" }}>
                                    <SkeletonContainer />
                                </div>
                            )}
                        </div> :
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={(e) => { setActiveField(e?.active?.data?.current?.field); }} onDragEnd={handleDndDragEnd} onDragOver={handleDndDragOver}>
                            <div style={{ height: "100%", overflowY: "auto", overflowX: "hidden" }}>
                                {Object.keys(displayJson?.current)?.map((area, index) => (
                                    <div key={index} className="px-3">
                                        <div className="d-flex align-items-center gap-3 fs-6 fw-bold">
                                            {area}
                                            <span className={`add-edit-btn description-tooltip-target-${area.replace(/\s/g, '')} c-pointer`}>
                                                <FontAwesomeIcon icon="fa-solid fa-circle-info" />
                                            </span>
                                        </div>
                                        <div>
                                            {displayJson?.fixed?.[area]?.map(field => (
                                                <div className="rounded field-box d-flex justify-content-between align-items-center bg-white border p-2 my-2" >
                                                    <span className="fw-semibold">{field?.custom_field_name}</span>
                                                </div>
                                            ))}
                                        </div>
                                        <SortableContext items={displayJson?.current?.[area]?.fields?.map((f) => f?.custom_field_id) || []} strategy={verticalListSortingStrategy} >
                                            <DroppableSection area={area} hoverArea={hoverArea} setHoverArea={setHoverArea} onDrop={onDrop} onDragOver={onDragOver}>
                                                {displayJson?.current?.[area]?.fields?.length === 0 ? (
                                                    <div className="text-muted text-center fw-semibold py-3 border-dashed">Drop fields here</div>
                                                ) : (displayJson?.current?.[area]?.fields?.map((field) => (
                                                    <SortableField key={field?.custom_field_id} field={field} area={area} removeFromSection={removeFromSection} collapsedId={collapsedId} />
                                                )))}
                                            </DroppableSection>
                                        </SortableContext>
                                        <Tooltip target={`.description-tooltip-target-${area.replace(/\s/g, '')}`} position="right" className="project-glance-tooltip w20">
                                            <div className="p-2">{displayJson?.current?.[area]?.info}</div>
                                        </Tooltip>
                                    </div>
                                ))}
                                <DragOverlay>
                                    {activeField ? (
                                        <div className="rounded field-box d-flex align-items-center bg-white border p-2 py-1 shadow" style={{ cursor: "grabbing", width: "250px", opacity: 0.9 }}>
                                            <span className="fw-semibold">
                                                <FontAwesomeIcon icon="fa-solid fa-grip-vertical" className="text-muted me-3" /> {activeField?.custom_field_name}
                                            </span>
                                        </div>
                                    ) : null}
                                </DragOverlay>
                            </div>
                        </DndContext>
                    }
                    <div className="mt-2 d-flex justify-content-end gap-1">
                        <button className="sk-btn sk-btn-primary" onClick={() => setDisplayJson((prev) => ({ ...prev, current: { ...prev?.api } }))}>Discard</button>
                        <button className="sk-btn sk-btn-primary" onClick={() => setCopy((prev) => ({ ...prev, visible: true }))}>Copy Layout</button>
                        {loader?.saveBtn ?
                            <button className="sk-btn sk-btn-primary bg-black text-white" style={{ width: "100px" }}>
                                <div className="spinner-border" role="status" style={{ width: "15px", height: "15px" }}>
                                    <span className="sr-only"></span>
                                </div>
                            </button>
                            : <button className="sk-btn sk-btn-primary bg-black text-white" onClick={() => saveLayout()}>Save Changes</button>
                        }
                    </div>
                </div>

                {/* Right: Available Fields List */}
                <div className="w30 bg-light py-1 px-3 border-start" style={{ height: "calc(100vh - 150px)" }}>
                    <div className="d-flex align-items-center gap-3 fs-6 fw-bold">
                        Fields
                        <span className="add-edit-btn"><FontAwesomeIcon icon="fa-solid fa-circle-info" className="field-tooltip-target c-pointer" /></span>
                    </div>
                    <div>
                        <input className="sk-input-dd w100" placeholder="Search fields..." value={fields?.search} onChange={(e) => setFields((prev) => ({ ...prev, search: e?.target?.value }))}/>
                    </div>
                    {loader?.fields ?
                        <div className="h-100 bg-white p-3 rounded border-top border-2 shadow-sm"><SkeletonLine numSkeletons={6} /></div>
                        :
                        <div className="p-3 rounded bg-white border-top border-2 shadow-sm" onDragOver={(e) => onDragOver(e, "FIELDS")} onDragLeave={() => setHoverArea(null)} onDrop={(e) => onDrop(e, null)} style={{ height: "90%", overflowY: "auto", minHeight: "200px" }}>
                            {Object.keys(fields?.list || {})?.map((group) => {
                                const visibleGroupFields = fields?.list?.[group]?.filter((field) => !usedIds?.has(field?.custom_field_id));
                                if (visibleGroupFields?.length === 0) return null;
                                return (
                                    <div key={group} className="mb-3">
                                        <div className="fw-bold my-2 small text-uppercase text-muted border-bottom pb-1">{group}</div>
                                        {visibleGroupFields?.map((field) => (
                                            <div key={field?.custom_field_id} draggable onDragStart={(e) => onDragStart(e, field, null)} onDrag={onDrag} onDragEnd={onDragEnd} className={`field-box rounded fw-semibold text-muted border bg-light  ${collapsedId === field?.custom_field_id ? "drag-source-hidden" : "p-2 my-2"}`} style={{ cursor: 'grab' }}>
                                                <FontAwesomeIcon icon="fa-solid fa-grip-vertical" className="text-muted me-3" /> {field?.custom_field_name}
                                            </div>
                                        ))}
                                    </div>
                                );
                            })}

                            {dragPreview && (
                                <div className="custom-drag-preview" style={{ left: dragPreview.x + 10, top: dragPreview.y + 10 }} >
                                    <FontAwesomeIcon icon="fa-solid fa-grip-vertical" className="text-muted me-2" /> {dragPreview.field?.custom_field_name}
                                </div>
                            )}

                            {usedIds?.size === 0 && (<p className="text-muted text-center fw-semibold mb-0 pt-2"> Drop fields here to make them available </p>)}
                        </div>
                    }
                    <Tooltip target=".field-tooltip-target" position="left" className="project-glance-tooltip" >
                        <div className="p-2"> Fields capture and display the information people need to carry out work. </div>
                    </Tooltip>
                </div>
            </div>
            <Dialog visible={copy?.visible} onHide={() => setCopy((prev) => ({ ...prev, visible: false, destination: [] }))} className="modal-small" draggable={false} header="Copy Layout">
                <div>
                    <MultiSelect options={copy?.opts} placeholder="Select screen scheme" optionLabel="screen_scheme_name" value={copy?.destination} onChange={(e) => setCopy((prev) => ({ ...prev, destination: e?.target?.value }))} panelHeaderTemplate={(options) => PanelHeaderTemplate(options, "scheme", copy?.destination, () => setCopy((prev) => ({ ...prev, destination: [] })))} />
                    <div className="d-flex gap-1 justify-content-end mt-2">
                        <button className="sk-btn sk-btn-primary" onClick={() => setCopy((prev) => ({ ...prev, visible: false, destination: [] }))}>Cancel</button>
                        {loader?.copyBtn ?
                            <button className="sk-btn sk-btn-primary bg-black text-white" style={{ width: "95px" }}>
                                <div className="spinner-border" role="status" style={{ width: "15px", height: "15px" }}>
                                    <span className="sr-only"></span>
                                </div>
                            </button>
                            : <button className="sk-btn sk-btn-primary bg-black text-white" onClick={() => copyLayout()}>Create Copy</button>
                        }
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

export default Configure;

